'use client';

import cx from 'classnames';
import { ReactNode } from 'react';

import NoData from '../NoData';
import { ArrowCursorPagination } from '../Pagination';
import { Loading } from '../Spinner';
import styles from './cardList.module.scss';

export const CardList = ({ children, className }: { className?: string; children?: ReactNode }) => {
  return <ul className={cx(styles.list, className)}>{children}</ul>;
};

export const SmartCardList = <I,>({
  isLoading,
  items,
  renderItem,
  className,
  noData = <NoData />,
}: {
  isLoading?: boolean;
  items?: I[] | undefined;
  renderItem: (item: I, index: number) => ReactNode;
  className?: string;
  noData?: ReactNode;
}) => {
  if (isLoading) return <Loading />;
  if (!items || items.length < 1) return noData;
  return <CardList className={className}>{items.map((item, i) => renderItem(item, i))}</CardList>;
};

export const SmartCardListWithPagination = <I,>({
  isLoading,
  items,
  renderItem,
  cursorPagination,
  className,
  noData = <NoData />,
}: {
  isLoading?: boolean;
  items?: I[] | undefined;
  renderItem: (item: I, index: number) => ReactNode;
  cursorPagination: {
    hasPrev: boolean;
    hasNext: boolean;
    cursorKey: keyof I;
    onChange: (params: { before?: string; after?: string }) => void;
  };
  className?: string;
  noData?: ReactNode;
}) => {
  if (isLoading) return <Loading />;
  if (!items || items.length < 1) return noData;

  return (
    <div className={cx(styles.wrapper, className)}>
      <CardList className={className}>{items.map((item, i) => renderItem(item, i))}</CardList>
      <ArrowCursorPagination
        className={styles.pagination}
        hasPrev={cursorPagination.hasPrev}
        hasNext={cursorPagination.hasNext}
        prevKey={cursorPagination.hasPrev ? (items[0][cursorPagination.cursorKey] as string) : undefined}
        nextKey={cursorPagination.hasNext ? (items[items.length - 1][cursorPagination.cursorKey] as string) : undefined}
        onChange={cursorPagination.onChange}
      />
    </div>
  );
};
